import type { FC, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Facebook, Search, TrendingUp, Zap } from "lucide-react";
import type {
  PaidMediaOverviewEfficiencyGap,
  PaidMediaOverviewPlatform,
  PaidMediaOverviewRevenueContributionLeader,
  PaidMediaOverviewSummaryPlatform,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewIndex,
  formatOverviewMultiplier,
  formatOverviewNumber,
  formatOverviewPct,
  formatOverviewText,
  OVERVIEW_EMPTY_VALUE,
} from "../../utils/PaidMediaOverviewFormat";
import { overviewPlatformLabel } from "../../utils/PaidMediaOverviewLabels";
import { OverviewMetricTip, OverviewShareBar } from "./OverviewPrimitives";

interface PlatformComparisonCardsProps {
  platforms: PaidMediaOverviewSummaryPlatform[];
  revenueContributionLeader: PaidMediaOverviewRevenueContributionLeader | null;
  efficiencyGap: PaidMediaOverviewEfficiencyGap | null;
}

const findPlatform = (
  platforms: PaidMediaOverviewSummaryPlatform[],
  platform: PaidMediaOverviewPlatform
): PaidMediaOverviewSummaryPlatform | undefined =>
  platforms?.find(entry => entry?.platform === platform);

const PlatformComparisonCards: FC<PlatformComparisonCardsProps> = ({
  platforms,
  revenueContributionLeader,
  efficiencyGap,
}) => {
  const { t } = useTranslation();

  const meta = findPlatform(platforms, "meta_ads");
  const google = findPlatform(platforms, "google_ads");

  const metaLabel = t("paidMediaOverview.platforms.meta");
  const googleLabel = t("paidMediaOverview.platforms.google");

  const leaderLabel = overviewPlatformLabel(t, revenueContributionLeader?.platform);
  const gapLeaderLabel = overviewPlatformLabel(t, efficiencyGap?.leading_platform);

  return (
    <div className="paid-media-overview__comparison-grid">
      <PlatformCard
        title={metaLabel}
        tooltipId="metaOverview"
        variant="meta-ads"
        platform="meta"
        icon={<Facebook size={16} />}
        summary={meta}
      />
      <PlatformCard
        title={googleLabel}
        tooltipId="googleOverview"
        variant="google-ads"
        platform="google"
        icon={<Search size={16} />}
        summary={google}
      />

      <article className="paid-media-panel paid-media-overview__insight-card">
        <div className="paid-media-overview__insight-card-header">
          <span className="paid-media-overview__insight-card-label">
            {t("paidMediaOverview.comparison.revenueLeaderLabel")}
            <OverviewMetricTip
              id="revenueLeader"
              label={t("paidMediaOverview.comparison.revenueLeaderLabel")}
            />
          </span>
          <TrendingUp size={16} className="paid-media-overview__insight-card-icon" aria-hidden />
        </div>
        <div className="paid-media-overview__insight-card-value">
          {leaderLabel || OVERVIEW_EMPTY_VALUE}
        </div>
        <p className="paid-media-overview__insight-card-copy">
          <Trans
            i18nKey="paidMediaOverview.comparison.revenueLeaderCopy"
            values={{
              revenueShare: formatOverviewPct(revenueContributionLeader?.revenue_share, 0),
              spendShare: formatOverviewPct(revenueContributionLeader?.spend_share, 0),
            }}
            components={{ b: <strong /> }}
          />
        </p>
        <div className="paid-media-overview__insight-card-callout paid-media-overview__insight-card-callout--positive">
          {t("paidMediaOverview.comparison.revenueLeaderIndex", {
            value: formatOverviewIndex(revenueContributionLeader?.efficiency_index),
          })}
        </div>
      </article>

      <article className="paid-media-panel paid-media-overview__insight-card">
        <div className="paid-media-overview__insight-card-header">
          <span className="paid-media-overview__insight-card-label">
            {t("paidMediaOverview.comparison.efficiencyGapLabel")}
            <OverviewMetricTip
              id="efficiencyGap"
              label={t("paidMediaOverview.comparison.efficiencyGapLabel")}
            />
          </span>
          <Zap size={16} className="paid-media-overview__insight-card-icon" aria-hidden />
        </div>
        <div className="paid-media-overview__insight-card-value">
          {gapLeaderLabel
            ? t("paidMediaOverview.comparison.efficiencyGapValue", {
                platform: gapLeaderLabel,
                value: formatOverviewMultiplier(efficiencyGap?.value),
              })
            : OVERVIEW_EMPTY_VALUE}
        </div>
        <p className="paid-media-overview__insight-card-copy">
          {t("paidMediaOverview.comparison.efficiencyGapCopy")}
        </p>
        <div className="paid-media-overview__insight-card-legend">
          <span>
            {metaLabel}{" "}
            <strong>{meta?.formatted_roas ?? formatOverviewMultiplier(meta?.roas)}</strong>
          </span>
          <span>
            {googleLabel}{" "}
            <strong>{google?.formatted_roas ?? formatOverviewMultiplier(google?.roas)}</strong>
          </span>
        </div>
      </article>
    </div>
  );
};

const PlatformCard: FC<{
  title: string;
  tooltipId: string;
  icon: ReactNode;
  summary: PaidMediaOverviewSummaryPlatform | undefined;
  platform: "meta" | "google";
  variant: "meta-ads" | "google-ads";
}> = ({ title, tooltipId, icon, summary, platform, variant }) => {
  const { t } = useTranslation();

  return (
    <article className="paid-media-panel paid-media-overview__platform-card">
      <div className="paid-media-overview__platform-card-header">
        <div className="paid-media-overview__platform-card-title">
          <span
            className={`paid-media-overview__platform-card-icon paid-media-overview__platform-card-icon--${platform}`}
            aria-hidden
          >
            {icon}
          </span>
          <span>
            {title}
            <OverviewMetricTip id={tooltipId} label={title} />
          </span>
        </div>
        <span className="paid-media-overview__platform-card-tag">
          {t("paidMediaOverview.comparison.tag")}
        </span>
      </div>
      <div className="paid-media-overview__platform-card-metrics">
        <Metric
          label={t("paidMediaOverview.metrics.spend")}
          value={formatOverviewText(summary?.formatted_spend)}
        />
        <Metric
          label={t("paidMediaOverview.metrics.revenue")}
          value={formatOverviewText(summary?.formatted_revenue)}
        />
        <Metric
          label={t("paidMediaOverview.metrics.purchases")}
          value={summary?.formatted_purchases ?? formatOverviewNumber(summary?.purchases)}
        />
        <Metric
          label={t("paidMediaOverview.metrics.roas")}
          value={summary?.formatted_roas ?? formatOverviewMultiplier(summary?.roas)}
        />
        <Metric
          label={t("paidMediaOverview.metrics.costPerPurchase")}
          value={formatOverviewText(summary?.formatted_cost_per_purchase)}
        />
      </div>
      <div className="paid-media-overview__platform-card-shares">
        <OverviewShareBar
          label={t("paidMediaOverview.comparison.shareOfTotalSpend")}
          value={summary?.spend_share ?? null}
          variant={variant}
          tooltipId="spendShare"
          right={summary?.formatted_spend_share ?? undefined}
        />
        <OverviewShareBar
          label={t("paidMediaOverview.comparison.shareOfPaidRevenue")}
          value={summary?.revenue_share ?? null}
          variant={variant}
          tooltipId="revenueShare"
          right={summary?.formatted_revenue_share ?? undefined}
        />
      </div>
    </article>
  );
};

const Metric: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="paid-media-overview__metric">
    <div className="paid-media-overview__metric-label">{label}</div>
    <div className="paid-media-overview__metric-value">{value}</div>
  </div>
);

export default PlatformComparisonCards;
